// Mind2Magic CRM — Sync Engine
// Extracted from index.html inline script (Phase 1, Step 1).
// Handles Supabase data loading, per-entity sync, bulk import, realtime subscriptions.

import { store } from './store.js'

// ========== LOAD FROM SUPABASE ==========

async function loadFromSupabase() {
    if (!supabaseDB.isConnected()) {
        console.log('Supabase not configured — running in demo mode');
        return false;
    }

    try {
        // Fetch all data in parallel
        const [
            leadsData,
            clientsData,
            teamData,
            tareasData,
            formSubs,
            companiesData,
            bookingsData,
            gsResult,
            waSummaries
        ] = await Promise.all([
            supabaseDB.fetchLeads(),
            supabaseDB.fetchClients(),
            supabaseDB.fetchTeamMembers(),
            supabaseDB.fetchTareas(),
            supabaseDB.fetchFormSubmissions('new'),
            supabaseDB.fetchCompanies(),
            supabaseDB.fetchCompanyBookings(),
            window.sb
                ? window.sb.from('guide_sessions').select('*').order('created_at', { ascending: false })
                : Promise.resolve({ data: null, error: null }),
            supabaseDB.fetchWhatsAppSummaries()
        ]);

        // Process leads
        if (leadsData) {
            store.leads = [];
            store.historico = [];
            leadsData.forEach(row => {
                const lead = supabaseDB._rowToLead(row);
                if (lead.isHistorico || lead.stage === 'historico') {
                    store.historico.push(lead);
                } else {
                    store.leads.push(lead);
                }
            });
        }

        // Process clients (depends on leads being processed above)
        if (clientsData) {
            store.clients = {};
            clientsData.forEach(row => {
                store.clients[row.client_key] = {
                    _supabaseId: row.id,
                    titular: row.titular,
                    contacto: row.contacto,
                    phone: row.phone,
                    email: row.email,
                    referido: row.referido,
                    canalAlerta: row.canal_alerta,
                    trips: [],
                    createdAt: row.created_at
                };
            });
            // Rebuild trips from leads
            [...store.leads, ...store.historico].forEach(lead => {
                if (lead.clientKey && store.clients[lead.clientKey]) {
                    store.clients[lead.clientKey].trips.push(lead);
                }
            });
        }

        // Process team members
        if (teamData && teamData.length > 0) {
            store.team = teamData.map(row => ({
                id: row.id,
                _supabaseId: row.id,
                nombre: row.nombre,
                rol: row.rol || '',
                email: row.email || '',
                orden: row.orden ?? null
            }));
        } else if (teamData && teamData.length === 0 && store.team.length > 0) {
            // First run: seed hardcoded fallback team to Supabase
            for (const m of store.team) {
                const data = await supabaseDB.upsertTeamMember(m);
                if (data) { m._supabaseId = data.id; m.id = data.id; }
            }
        }

        // Process tareas
        if (tareasData) {
            store.tareas = tareasData.map(row => supabaseDB._rowToTarea(row));
        }

        // Process pending form submissions
        if (formSubs) {
            store.pendingFormSubmissions = formSubs;
            store.allFormSubmissions = [...formSubs];
            store.formSubsLoaded = true;
        }

        // Process companies (B2B)
        if (companiesData) {
            store.companies = companiesData.map(row => supabaseDB._rowToCompany(row));
        }

        // Process company bookings (B2B)
        if (bookingsData) {
            store.companyBookings = bookingsData.map(row => supabaseDB._rowToBooking(row));
        }

        // Process guide sessions
        const { data: gsData, error: gsError } = gsResult;
        if (gsError) console.error('fetchGuideSessions error:', gsError);
        if (gsData) {
            store.allGuideSessions = gsData;
            gsData.forEach(gs => {
                const lead = [...store.leads, ...store.historico].find(l => l._supabaseId === gs.lead_id);
                if (lead) lead._guideSession = gs;
            });
        }

        // Process WhatsApp summaries for pipeline badges
        if (waSummaries) store.waLeadSummaries = waSummaries;

        // Update WhatsApp inbox badge after data load
        setTimeout(() => {
            window.checkColdConversations?.();
            window.updateWhatsAppInboxBadge?.();
        }, 5000);

        console.log(`Loaded from Supabase: ${store.leads.length} leads, ${store.historico.length} historico, ${Object.keys(store.clients).length} clients, ${store.tareas.length} tareas, ${store.companies.length} companies, ${store.companyBookings.length} bookings, ${store.pendingFormSubmissions.length} pending forms, ${store.allGuideSessions.length} guide sessions`);
        return true;
    } catch (err) {
        console.error('Error loading from Supabase:', err);
        return false;
    }
}


// ========== SYNC INDIVIDUAL ENTITIES ==========

function syncLeadToSupabase(lead) {
    if (!supabaseDB.isConnected()) return;
    const sid = lead._supabaseId;
    if (sid) { store._recentSyncs.add(sid); setTimeout(() => store._recentSyncs.delete(sid), 15000); }
    supabaseDB.upsertLead(lead).then(data => {
        if (data && !lead._supabaseId) {
            const oldId = lead.id;
            lead._supabaseId = data.id;
            lead.id = data.id;
            if (lead.clientKey && store.clients[lead.clientKey]) {
                const t = store.clients[lead.clientKey].trips.find(t => t.id === oldId);
                if (t) { t._supabaseId = data.id; t.id = data.id; }
            }
            store._recentSyncs.add(data.id); setTimeout(() => store._recentSyncs.delete(data.id), 15000);
            renderAll(); // Re-render cards with new UUID (onclick was baked with old temp ID)
        }
        // Auto-link any unmatched WhatsApp messages that match this lead's phone
        if (lead.phone) window.autoLinkForLead?.(lead);
    }).catch(err => { console.error('syncLead error:', err); toast('Error sincronizando lead — reintenta o recarga', 'error'); });
}

function syncClientToSupabase(clientKey, clientData) {
    if (!supabaseDB.isConnected()) return;
    supabaseDB.upsertClient(clientKey, clientData).then(data => {
        if (data && !clientData._supabaseId) { clientData._supabaseId = data.id; }
    }).catch(err => { console.error('syncClient error:', err); toast('Error sincronizando cliente', 'error'); });
}

function syncTareaToSupabase(tarea) {
    if (!supabaseDB.isConnected()) return;
    supabaseDB.upsertTarea(tarea).catch(err => { console.error('syncTarea error:', err); toast('Error sincronizando tarea', 'error'); });
}

function syncCompanyToSupabase(company) {
    if (!supabaseDB.isConnected()) return;
    supabaseDB.upsertCompany(company).then(data => {
        if (data && !company._supabaseId) { company._supabaseId = data.id; company.id = data.id; }
    }).catch(err => { console.error('syncCompany error:', err); toast('Error sincronizando empresa', 'error'); });
}

function syncBookingToSupabase(booking) {
    if (!supabaseDB.isConnected()) return;
    supabaseDB.upsertBooking(booking).then(data => {
        if (data && !booking._supabaseId) { booking._supabaseId = data.id; booking.id = data.id; }
    }).catch(err => { console.error('syncBooking error:', err); toast('Error sincronizando reserva', 'error'); });
}


// ========== BULK SYNC PROGRESS ==========

function showSyncProgress(current, total, phase) {
    let bar = document.getElementById('syncProgressBar');
    if (!bar) {
        bar = document.createElement('div');
        bar.id = 'syncProgressBar';
        bar.style.cssText = 'position:fixed;bottom:20px;right:20px;background:var(--bg-1);border:1px solid var(--border);border-radius:12px;padding:16px 20px;min-width:280px;z-index:1999;box-shadow:0 8px 32px rgba(0,0,0,0.3);transition:opacity 0.3s;';
        document.body.appendChild(bar);
    }
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    bar.innerHTML = `
        <div style="font-size:12px;color:var(--text-2);margin-bottom:6px;">${phase}</div>
        <div style="display:flex;align-items:center;gap:10px;">
            <div style="flex:1;height:6px;background:var(--bg-3);border-radius:3px;overflow:hidden;">
                <div style="width:${pct}%;height:100%;background:var(--gradient);border-radius:3px;transition:width 0.3s;"></div>
            </div>
            <span style="font-size:11px;color:var(--text-3);white-space:nowrap;">${current}/${total}</span>
        </div>`;
}

function hideSyncProgress() {
    const bar = document.getElementById('syncProgressBar');
    if (bar) { bar.style.opacity = '0'; setTimeout(() => bar.remove(), 300); }
}

async function syncBulkImportToSupabase() {
    if (!supabaseDB.isConnected()) return;
    try {
        // Phase 1: Sync only new clients (no _supabaseId)
        const clientEntries = Object.entries(store.clients).filter(([key, c]) => !c._supabaseId);
        let clientCount = 0;
        if (clientEntries.length > 0) {
            for (let i = 0; i < clientEntries.length; i++) {
                const [key, client] = clientEntries[i];
                const result = await supabaseDB.upsertClient(key, client);
                if (result) { clientCount++; client._supabaseId = result.id; }
                if (i % 10 === 0) showSyncProgress(i + 1, clientEntries.length, 'Sincronizando clientes nuevos...');
            }
        }
        showSyncProgress(clientEntries.length || 1, clientEntries.length || 1, clientEntries.length > 0 ? 'Clientes sincronizados' : 'Clientes ya sincronizados');

        // Phase 2: Sync leads
        const allLeads = [...store.leads, ...store.historico];
        const newLeads = allLeads.filter(l => !l._supabaseId);
        let syncedCount = 0;
        if (newLeads.length > 0) {
            const batchSize = 50;
            const totalBatches = Math.ceil(newLeads.length / batchSize);
            showSyncProgress(0, newLeads.length, 'Sincronizando leads...');

            const result = await supabaseDB.insertLeads(newLeads, (batchNum, batchTotal, inserted) => {
                showSyncProgress(Math.min(batchNum * batchSize, newLeads.length), newLeads.length, `Sincronizando leads... (lote ${batchNum}/${batchTotal})`);
            });
            if (result) {
                syncedCount = result.length;
                result.forEach(row => {
                    // Match by reservation number first (most reliable), then by name+phone
                    const match = allLeads.find(l =>
                        !l._supabaseId && (
                            (l.reservationNum && row.reservation_num && l.reservationNum === row.reservation_num) ||
                            (l.titular === row.titular && l.phone === row.phone)
                        )
                    );
                    if (match) {
                        match._supabaseId = row.id;
                        match.id = row.id;
                    }
                });
            }
        }
        const alreadySynced = allLeads.length - newLeads.length;
        hideSyncProgress();
        renderAll();
        toast(`${syncedCount} leads sincronizados, ${alreadySynced} ya existían (${clientCount} clientes)`, 'success');
    } catch (err) {
        console.error('Bulk sync error:', err);
        hideSyncProgress();
        toast('Error al sincronizar: ' + err.message, 'error');
    }
}


// ========== REALTIME SUBSCRIPTIONS ==========

function setupRealtimeSubscriptions() {
    if (!supabaseDB.isConnected()) return;

    supabaseDB.subscribeToChanges({
        // onLeadChange
        onLeadChange: async (payload) => {
            if (payload.eventType === 'UPDATE') {
                if (store._recentSyncs.has(payload.new.id)) return;
                const updated = supabaseDB._rowToLead(payload.new);
                const idx = store.leads.findIndex(l => l._supabaseId === updated._supabaseId);
                if (idx >= 0) {
                    store.leads[idx] = { ...store.leads[idx], ...updated };
                } else {
                    const hIdx = store.historico.findIndex(l => l._supabaseId === updated._supabaseId);
                    if (hIdx >= 0) store.historico[hIdx] = { ...store.historico[hIdx], ...updated };
                }
                renderAll();
            } else if (payload.eventType === 'INSERT') {
                if (store._recentSyncs.has(payload.new.id)) return;
                const newLead = supabaseDB._rowToLead(payload.new);
                // Check if this lead was recently deleted locally (suppress ghost re-insert)
                const delKey = `del_${(newLead.titular || '').toLowerCase()}_${newLead.phone || ''}`
                if (store._recentSyncs.has(delKey)) {
                    // Lead was deleted locally — also delete from Supabase to clean up
                    supabaseDB.deleteLead(payload.new.id).catch(err => console.error('Ghost delete error:', err))
                    return;
                }
                if (store.leads.find(l => l._supabaseId === newLead._supabaseId) || store.historico.find(l => l._supabaseId === newLead._supabaseId)) return;
                // Check self-echo suppress key from createLeadFromSubmission
                const newKey = `new_${(newLead.titular || '').toLowerCase()}_${newLead.phone || ''}`
                const localMatch = store.leads.find(l => !l._supabaseId && l.titular === newLead.titular && l.phone === newLead.phone && l.source === newLead.source);
                if (localMatch) {
                    localMatch._supabaseId = newLead._supabaseId;
                    localMatch.id = newLead._supabaseId;
                    store._recentSyncs.add(newLead._supabaseId); setTimeout(() => store._recentSyncs.delete(newLead._supabaseId), 15000)
                    // Don't renderAll here — the upsert .then() callback will do it once with stable ID
                    if (store._recentSyncs.has(newKey)) return;
                    renderAll();
                    return;
                }
                if (newLead.isHistorico) store.historico.push(newLead);
                else store.leads.push(newLead);
                renderAll();
            } else if (payload.eventType === 'DELETE') {
                if (store._recentSyncs.has(payload.old.id)) return;
                store.leads = store.leads.filter(l => l._supabaseId !== payload.old.id);
                store.historico = store.historico.filter(l => l._supabaseId !== payload.old.id);
                renderAll();
            }
        },
        // onTareaChange
        onTareaChange: async (payload) => {
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const updated = supabaseDB._rowToTarea(payload.new);
                const idx = store.tareas.findIndex(t => t._supabaseId === updated._supabaseId);
                if (idx >= 0) store.tareas[idx] = updated;
                else store.tareas.push(updated);
                renderTareas();
            } else if (payload.eventType === 'DELETE') {
                store.tareas = store.tareas.filter(t => t._supabaseId !== payload.old.id);
                renderTareas();
            }
        },
        // onFormSubmission — auto-create lead in pipeline
        onFormSubmission: (payload) => {
            const sub = payload.new;
            if (!store.pendingFormSubmissions.find(s => s.id === sub.id)) store.pendingFormSubmissions.push(sub);
            if (!store.allFormSubmissions.find(s => s.id === sub.id)) store.allFormSubmissions.unshift(sub);
            updateFormBadge();
            if (sub.status === 'new' && !sub.lead_id && !store._processedSubmissionIds.has(sub.id)) {
                setTimeout(() => createLeadFromSubmission(sub.id), 500);
            }
            const _subName = sub.data?.nombre || sub.data?.name || 'Nuevo viajero'
            toast(`📝 ${_subName} envió una solicitud de cotización`, 'success', { persistent: true });
            if (document.getElementById('formulariosView')?.classList.contains('active')) renderFormularios();
        },
        // onFormSession — form_sessions realtime
        onFormSession: (payload) => {
            if (payload.eventType === 'INSERT') {
                if (store.formSessionsLoaded) {
                    const exists = store.allFormSessions.find(s => s.session_id === payload.new.session_id);
                    if (!exists) store.allFormSessions.unshift(payload.new);
                }
            } else if (payload.eventType === 'UPDATE') {
                const idx = store.allFormSessions.findIndex(s => s.id === payload.new.id);
                if (idx >= 0) store.allFormSessions[idx] = { ...store.allFormSessions[idx], ...payload.new };
                else if (store.formSessionsLoaded) store.allFormSessions.unshift(payload.new);
            }
            if (document.getElementById('formulariosView')?.classList.contains('active')) renderFormularios();
        },
        // onGuideSession — guide_sessions realtime
        onGuideSession: (payload) => {
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                const gs = payload.new;
                const idx = store.allGuideSessions.findIndex(g => g.id === gs.id);
                if (idx >= 0) store.allGuideSessions[idx] = gs;
                else store.allGuideSessions.push(gs);
                const lead = [...store.leads, ...store.historico].find(l => l._supabaseId === gs.lead_id);
                if (lead) {
                    lead._guideSession = gs;
                    if (gs.status === 'completed' && gs.responses) {
                        lead.formData = lead.formData || {};
                        lead.formData.guideResponses = gs.responses;
                        syncLeadToSupabase(lead);
                        const leadId = lead.id;
                        toast(`✨ ${lead.titular} completó su itinerario personalizado`, 'success', { persistent: true, onClick: () => openLead(leadId) });
                    }
                    if (store.currentLeadId === lead.id) openLead(lead.id);
                    renderAll();
                }
            }
        },
        // onBookingChange — company_bookings realtime
        onBookingChange: (payload) => {
            if (payload.eventType === 'UPDATE') {
                const updated = supabaseDB._rowToBooking(payload.new);
                const idx = store.companyBookings.findIndex(b => b._supabaseId === updated._supabaseId);
                if (idx >= 0) store.companyBookings[idx] = { ...store.companyBookings[idx], ...updated };
                if (store.crmMode === 'empresas') renderEmpPipeline();
            } else if (payload.eventType === 'INSERT') {
                const nb = supabaseDB._rowToBooking(payload.new);
                if (!store.companyBookings.find(b => b._supabaseId === nb._supabaseId)) {
                    store.companyBookings.push(nb);
                    if (store.crmMode === 'empresas') renderEmpPipeline();
                }
            } else if (payload.eventType === 'DELETE') {
                store.companyBookings = store.companyBookings.filter(b => b._supabaseId !== payload.old.id);
                if (store.crmMode === 'empresas') renderEmpPipeline();
            }
        },
        // onDashboardChange — client_dashboards realtime (feedback detection)
        onDashboardChange: (payload) => {
            if (payload.eventType === 'UPDATE') {
                const row = payload.new;
                // Update cache
                const leadId = [...store.leads, ...store.historico].find(l => l._supabaseId === row.lead_id)?.id;
                if (leadId) {
                    store._dashboardCache[leadId] = row;
                    // If feedback just submitted, show toast
                    if (row.feedback_submitted_at && !store._dashboardCache[leadId + '_notified']) {
                        store._dashboardCache[leadId + '_notified'] = true;
                        const lead = store.leads.find(l => l.id === leadId) || store.historico.find(l => l.id === leadId);
                        const name = lead?.titular || 'Cliente';
                        toast(`📊 ${name} envió su feedback de satisfacción`, 'success', {
                            onClick: () => { if (lead) window.openLead(leadId) }
                        });
                        // Persist feedback to lead formData
                        if (lead && row.feedback) {
                            lead.formData = lead.formData || {};
                            lead.formData._feedback = row.feedback;
                            syncLeadToSupabase(lead);
                        }
                    }
                    renderAll();
                }
            }
        },
        // onWhatsAppMessage — whatsapp_messages realtime (new incoming messages)
        onWhatsAppMessage: (payload) => {
            const msg = payload.new;
            if (!msg) return;

            if (msg.lead_id) {
                // Add to cached messages for this lead
                if (!store.whatsappMessages[msg.lead_id]) {
                    store.whatsappMessages[msg.lead_id] = [];
                }
                // Dedup by id
                if (!store.whatsappMessages[msg.lead_id].some(m => m.id === msg.id)) {
                    store.whatsappMessages[msg.lead_id].push(msg);
                }

                if (msg.lead_id) {
                    store.waLeadSummaries[msg.lead_id] = {
                        lastMessageAt: msg.created_at,
                        lastDirection: msg.direction || 'in',
                        messageCount: (store.waLeadSummaries[msg.lead_id]?.messageCount || 0) + 1
                    };
                }

                // If this lead's panel is open, re-render WhatsApp section
                const lead = [...store.leads, ...store.historico].find(l => l._supabaseId === msg.lead_id);
                if (lead && lead.id === store.currentLeadId) {
                    const waContainer = document.getElementById('waMessagesContainer');
                    if (waContainer && window.renderWhatsAppMessages) {
                        waContainer.innerHTML = window.renderWhatsAppMessages(store.whatsappMessages[msg.lead_id]);
                        waContainer.style.display = 'block';
                        waContainer.scrollTop = waContainer.scrollHeight;
                    }
                }

                // Toast notification
                const name = lead?.titular || msg.phone_number;
                toast(`WhatsApp de ${name}: "${(msg.content || '').substring(0, 50)}"`, 'success', {
                    onClick: () => { if (lead) window.openLead(lead.id); }
                });
                window.updateWhatsAppInboxBadge?.();
                // Live-refresh inbox if WhatsApp view is active
                if (document.getElementById('whatsappView')?.classList.contains('active')) {
                    window.renderWhatsAppInbox?.();
                }
            } else {
                // Unmatched message — try auto-link first
                if (!store.unmatchedWaMessages.some(m => m.id === msg.id)) {
                    store.unmatchedWaMessages.unshift(msg);
                }
                // Attempt auto-link against existing leads (non-blocking)
                if (window.autoLinkSinglePhone) {
                    window.autoLinkSinglePhone(msg.phone_number).then(linked => {
                        if (!linked) {
                            window.updateWhatsAppBadge?.();
                            toast(`WhatsApp de ${msg.phone_number}: "${(msg.content || '').substring(0, 50)}"`, 'warning', {
                                onClick: () => window.openWhatsAppPanel?.()
                            });
                        }
                        window.updateWhatsAppInboxBadge?.();
                        // Live-refresh inbox if WhatsApp view is active
                        if (document.getElementById('whatsappView')?.classList.contains('active')) {
                            window.renderWhatsAppInbox?.();
                        }
                    });
                } else {
                    window.updateWhatsAppBadge?.();
                    toast(`WhatsApp de ${msg.phone_number}: "${(msg.content || '').substring(0, 50)}"`, 'warning', {
                        onClick: () => window.openWhatsAppPanel?.()
                    });
                    window.updateWhatsAppInboxBadge?.();
                    // Live-refresh inbox if WhatsApp view is active
                    if (document.getElementById('whatsappView')?.classList.contains('active')) {
                        window.renderWhatsAppInbox?.();
                    }
                }
            }
        }
    });
}


// ========== FORM BADGE ==========

function updateFormBadge() {
    const badge = document.getElementById('badgeFormularios');
    if (!badge) return;
    let count;
    if (store.formSubsLoaded) count = store.allFormSubmissions.filter(s => s.status === 'new').length;
    else count = store.pendingFormSubmissions.length;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'inline' : 'none';
    // Also update legacy badge if exists
    const old = document.getElementById('formSubmissionsBadge');
    if (old) { old.textContent = count; old.style.display = count > 0 ? 'inline' : 'none'; }
}


// ========== WINDOW BRIDGE ==========
Object.assign(window, {
    loadFromSupabase,
    syncLeadToSupabase,
    syncClientToSupabase,
    syncTareaToSupabase,
    syncCompanyToSupabase,
    syncBookingToSupabase,
    showSyncProgress,
    hideSyncProgress,
    syncBulkImportToSupabase,
    setupRealtimeSubscriptions,
    updateFormBadge,
});
